import React, { useEffect, useState } from "react";
import { useSession } from "Context/userAuthContext";
import { useNavigate, useParams } from "react-router-dom";

import Masonry, { ResponsiveMasonry } from "react-responsive-masonry";
import {
  Box,
  Button,
  Typography,
  Stack,
  Toolbar,
  AppBar,
  Select,
  MenuItem,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  SelectChangeEvent,
  Skeleton,
} from "@mui/material";
import { LabeledText } from "Components/LabeledText";
import { CreatedWith, InfoBox, SignInBtn } from "Components/AllComponents";
import Logo from "Assets/logo-full.svg";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { InspirationItem } from "Model/Inspiration";
import Thumb from "Assets/thumbnail.png";
import { useAppDispatch, useAppSelector } from "Redux/hooks";
import { fetchInspirationTopics } from "Redux/Reducers/inspirations";
import { topicsSelector } from "Redux/selectors";
import { sl } from "date-fns/locale";
import { Divider } from "semantic-ui-react";

const Discover = () => {
  const { topic } = useParams<{ topic: string }>();
  const dispatch = useAppDispatch();
  const inspirationTopics = useAppSelector(topicsSelector);
  const [selectedTopic, setSelectedTopic] = useState<string>("");
  const [pins, setPins] = useState<InspirationItem[]>([]);
  const [loadedImages, setLoadedImages] = useState<Set<string>>(new Set());

  // Fetch topics when the component mounts
  useEffect(() => {
    dispatch(fetchInspirationTopics());
  }, [dispatch]);

  // Update pins when the selected topic changes
  useEffect(() => {
    if (inspirationTopics.length > 0) {
      // If a topic is specified in URL, use that
      if (topic) {
        const lowerCaseTopic = topic.toLowerCase();
        const selectedInspiration = inspirationTopics.find(
          (inspiration) => inspiration.name?.toLowerCase() === lowerCaseTopic
        );
        if (selectedInspiration) {
          setSelectedTopic(selectedInspiration.name);
          setPins(selectedInspiration.items || []);
          return;
        }
      }

      // If no topic in URL or not found, use default topic
      const defaultTopic = inspirationTopics.find((t) => t.isDefault);
      if (defaultTopic) {
        setSelectedTopic(defaultTopic.name);
        setPins(defaultTopic.items || []);
        return;
      }

      // If no default topic, fall back to first topic
      setSelectedTopic(inspirationTopics[0]?.name || "");
      setPins(inspirationTopics[0]?.items || []);
    }
  }, [topic, inspirationTopics]);

  // Reset loaded images when pins change
  useEffect(() => {
    setLoadedImages(new Set());
  }, [pins]);

  const handleChange = (event: SelectChangeEvent<string>) => {
    const selectedName = event.target.value as string;
    setSelectedTopic(selectedName);
    const selectedInspiration = inspirationTopics.find(
      (inspiration) => inspiration.name === selectedName
    );
    if (selectedInspiration) {
      setPins(selectedInspiration.items || []);
    }
  };

  // Helper function to build a proper URL
  const buildProperUrl = (url: string): string => {
    if (url.startsWith("http://") || url.startsWith("https://")) {
      return url;
    }
    return `http://${url}`;
  };

  const { user } = useSession();
  const navigate = useNavigate();

  const [open, setOpen] = React.useState(false);
  const [currentPin, setCurrentPin] = useState({} as InspirationItem);
  const handleClickOpen = () => {
    setOpen(true);
  };
  const handleClose = () => {
    setOpen(false);
  };

  const handleImageLoad = (href: string) => {
    setLoadedImages((prev) => new Set(prev).add(href));
  };

  return (
    <Stack
      sx={{
        p: { xs: 2, sm: 3 },
        backgroundColor: "background.default",
        minHeight: "100vh",
      }}
    >
      <AppBar sx={{ backgroundColor: "background.default" }}>
        <Toolbar variant="dense">
          <Typography variant="h6" color="text.primary" flex={1}>
            Inspirations
          </Typography>
          <Stack direction="row" spacing={1}>
            {user ? (
              <Stack direction="row" spacing={1}>
                <Button
                  variant="outlined"
                  onClick={async () => {
                    navigate("/admin/presales");
                  }}
                >
                  My presales
                </Button>
              </Stack>
            ) : (
              <Stack direction="row" spacing={1}>
                <CreatedWith color="dark" />
              </Stack>
            )}
          </Stack>
        </Toolbar>
      </AppBar>

      <Stack sx={{ mt: 6 }} />

      <Typography variant="h5" sx={{ mb: 3 }}>
        Get inspired for your next{" "}
        <Select
          value={selectedTopic}
          onChange={handleChange}
          displayEmpty
          sx={{
            ml: 1,
            mr: 1,
            fontFamily: "Lora",
            fontWeight: 600,
            fontSize: "1.7rem",
            "& .MuiInputBase-input": {
              padding: 1,
            },
          }}
        >
          {inspirationTopics.map((topic) => (
            <MenuItem key={topic.id} value={topic.name}>
              {topic.name}
            </MenuItem>
          ))}
        </Select>
        presale:
      </Typography>

      <ResponsiveMasonry
        columnsCountBreakPoints={{ 350: 2, 750: 3, 900: 4, 1200: 5, 1500: 7 }}
      >
        <Masonry gutter="12px">
          {pins.map((item) => (
            <Stack
              onClick={() => {
                setCurrentPin(item);
                // if (
                //   item.link !== "" &&
                //   !item.link.startsWith("https://flowlylink.com/")
                // ) {
                //   window.open(buildProperUrl(item.link), "_blank");
                // } else {
                //   handleClickOpen();
                // }
                handleClickOpen();
              }}
              sx={{ "&:hover": { cursor: "pointer", opacity: 0.85 } }}
              key={item.href}
            >
              <img
                style={{
                  width: "100%",
                  display: "block",
                  borderRadius: "20px",
                }}
                src={item.img}
                alt=""
                loading="lazy"
              />
            </Stack>
          ))}
        </Masonry>
      </ResponsiveMasonry>

      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle
          sx={{ m: 0, p: 2, color: "white" }}
          id="customized-dialog-title"
        >
          ITEM NAME
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={handleClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 12,
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
        <DialogContent sx={{ pt: 0 }}>
          <img
            style={{
              width: "100%",
              display: "block",
              marginBottom: "16px",
            }}
            src={currentPin.img}
            alt=""
          />
          {/* {currentPin.title !== " " ? (
            <Typography variant="subtitle2">{currentPin.title}</Typography>
          ) : null}
          {currentPin.price !== null ? (
            <Typography variant="subtitle2">${currentPin.price}</Typography>
          ) : null} */}
          {currentPin.description !== " " ? (
            <Stack spacing={1}>
              <LabeledText
                keepTextFormatting
                label="Cutters"
                text={currentPin.suppliers?.cutterSellers?.join(", ") || "--"}
              />
              <LabeledText
                keepTextFormatting
                label="Packaging"
                text={
                  currentPin.suppliers?.packagingSellers?.join(", ") || "--"
                }
              />
              <LabeledText
                keepTextFormatting
                label="Other suppliers"
                text={currentPin.suppliers?.otherSuppliers?.join(", ") || "--"}
              />
            </Stack>
          ) : null}
        </DialogContent>
      </Dialog>
    </Stack>
  );
};

export default Discover;
